/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  BoltIcon,
  ClockIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  SignalIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { getHistory, getServers, runSpeedTest } from "@/api/speedtest";
import { getMonitorAgents } from "@/api/monitor";
import { SpeedByServer, SpeedCharts } from "./SpeedByServer";
import { groupByServer, pingOf } from "./speedStats";
import { useSpeedSource } from "@/utils/speedSource";
import {
  createPacketLossMonitor,
  deletePacketLossMonitor,
  getPacketLossHistory,
  getPacketLossMonitors,
  startPacketLossMonitor,
  stopPacketLossMonitor,
  updatePacketLossMonitor,
} from "@/api/packetloss";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PacketLossMonitorForm } from "./packetloss/PacketLossMonitorForm";
import { PacketLossMonitorDetails } from "./packetloss/PacketLossMonitorDetails";
import { usePacketLossMonitorStatus } from "./packetloss/hooks/usePacketLossMonitorStatus";
import { defaultFormData, type MonitorFormData } from "./packetloss/constants/packetLossConstants";
import { getAnomalies } from "@/api/overview";
import { PaginatedResponse, PacketLossMonitor, PacketLossResult, SpeedTestResult } from "@/types/types";
import { Sparkline } from "@/components/common/Sparkline";
import { showToast } from "@/components/common/Toast";
import { cn } from "@/lib/utils";
import { ScheduleOverview } from "./ScheduleOverview";
import { LiveRequestsCard } from "./LiveRequestsCard";

type Tone = "ok" | "warn" | "crit" | "idle";

const TONE_TEXT: Record<Tone, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  crit: "text-red-600 dark:text-red-400",
  idle: "text-gray-500 dark:text-gray-400",
};
const TONE_DOT: Record<Tone, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  crit: "bg-red-500",
  idle: "bg-gray-400",
};
const TONE_RING: Record<Tone, string> = {
  ok: "border-gray-200 dark:border-white/10",
  warn: "border-amber-500/40",
  crit: "border-red-500/40",
  idle: "border-gray-200 dark:border-white/10",
};

const card = "rounded-xl border bg-white dark:bg-white/[0.03]";

function mbps(n: number): string {
  if (!(n >= 0.5)) return "—"; // a zero (failed leg) shouldn't read as "-0 Mbps" next to a baseline
  return n >= 1000 ? `${(n / 1000).toFixed(2)} Gbps` : `${n.toFixed(0)} Mbps`;
}

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: Tone;
}> = ({ icon, label, value, sub, tone = "ok" }) => (
  <div className={cn(card, "px-4 py-3", TONE_RING[tone])}>
    <div className="flex items-center gap-2 text-[12px] font-medium text-gray-500 dark:text-gray-400">
      {icon}
      {label}
    </div>
    <div className="mt-1.5 text-[26px] font-semibold leading-none tabular-nums text-gray-900 dark:text-white">
      {value}
    </div>
    {sub && <div className="mt-1.5 text-[12px] text-gray-500 dark:text-gray-400">{sub}</div>}
  </div>
);

/** Δ vs the average of the earlier tests, as a coloured chip (higher is better unless `invert`). */
const Delta: React.FC<{ now: number; base: number; invert?: boolean }> = ({ now, base, invert }) => {
  if (!base) return <span>no baseline yet</span>;
  const pct = ((now - base) / base) * 100;
  const good = invert ? pct <= 0 : pct >= 0;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("font-semibold tabular-nums", Math.abs(pct) < 3 ? "" : good ? TONE_TEXT.ok : TONE_TEXT.crit)}>
        {pct >= 0 ? "+" : ""}
        {pct.toFixed(0)}%
      </span>
      vs average
    </span>
  );
};

interface Props {
  onOpen: (tool: "speedtest" | "traceroute" | "requests") => void;
}

export const NetworkOverview: React.FC<Props> = ({ onOpen }) => {
  const queryClient = useQueryClient();

  // Where a test runs: the panel's own host (aeza) or one of the monitor agents (pve, at home).
  const { data: agents = [] } = useQuery({ queryKey: ["monitor-agents"], queryFn: getMonitorAgents, staleTime: 60_000 });
  const locations = useMemo(() => {
    // an agent listening on port 8200 of localhost is the one on the panel's own machine
    const localAgent = agents.find((a) => /127\.0\.0\.1:8200\b/.test(a.url ?? ""));
    const list: { key: string; label: string; agentId?: number }[] = [{ key: "local", label: localAgent?.name ?? "Panel host" }];
    for (const a of agents) {
      if (a.enabled && a.id !== localAgent?.id) list.push({ key: String(a.id), label: a.name, agentId: a.id });
    }
    return list.slice(0, 6); // a handful of machines is plenty for a comparison strip
  }, [agents]);
  const speedHistories = useQueries({
    queries: locations.map((l) => ({
      queryKey: ["net-overview-speed", l.key],
      queryFn: () => getHistory("all", 1, 300, l.agentId ? String(l.agentId) : "local") as Promise<PaginatedResponse<SpeedTestResult>>,
      refetchInterval: 60_000,
      retry: false,
    })),
  });
  const [where, setWhere] = useSpeedSource();
  const loc = locations.find((l) => l.key === where) ?? locations[0];
  const locIndex = Math.max(0, locations.findIndex((l) => l.key === loc.key));
  const speed = speedHistories[locIndex]?.data;
  const { data: monitors = [] } = useQuery({
    queryKey: ["packetloss-monitors"],
    queryFn: getPacketLossMonitors,
    refetchInterval: 60_000,
  });
  const { data: anomalies = [] } = useQuery({
    queryKey: ["anomalies"],
    queryFn: getAnomalies,
    refetchInterval: 30_000,
  });

  // ---- monitor management, right on the cards ----
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PacketLossMonitor | null>(null);
  const [formData, setFormData] = useState<MonitorFormData>(defaultFormData);
  const [detail, setDetail] = useState<PacketLossMonitor | null>(null);
  const [removing, setRemoving] = useState<PacketLossMonitor | null>(null);
  const monitorStatuses = usePacketLossMonitorStatus(monitors, detail?.id);

  const refreshMonitors = () => {
    queryClient.invalidateQueries({ queryKey: ["packetloss-monitors"] });
    queryClient.invalidateQueries({ queryKey: ["packetloss", "monitors"] });
    queryClient.invalidateQueries({ queryKey: ["net-overview-pl"] });
  };
  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData(defaultFormData);
  };
  const fail = (what: string) => (e: unknown) =>
    showToast(what, "error", { description: e instanceof Error ? e.message : "Unknown error" });

  const saveMonitor = useMutation({
    mutationFn: async (data: MonitorFormData) => {
      // "exact" schedules are stored as "exact:HH:MM,HH:MM" in the interval field
      const interval =
        data.scheduleType === "exact" && data.exactTimes && data.exactTimes.length > 0
          ? `exact:${data.exactTimes.join(",")}`
          : data.interval;
      const body = { ...data, interval, scheduleType: undefined, exactTimes: undefined };
      if (editing) return updatePacketLossMonitor({ ...editing, ...body });
      const created = await createPacketLossMonitor(body);
      if (data.enabled && created?.id) await startPacketLossMonitor(created.id).catch(() => undefined);
      return created;
    },
    onSuccess: () => {
      showToast(editing ? "Monitor updated" : "Monitor created", "success");
      closeForm();
      refreshMonitors();
    },
    onError: fail("Could not save the monitor"),
  });
  const toggleMonitor = useMutation({
    mutationFn: (m: PacketLossMonitor) => (m.enabled ? stopPacketLossMonitor(m.id) : startPacketLossMonitor(m.id)),
    onSuccess: refreshMonitors,
    onError: fail("Could not change the monitor"),
  });
  const removeMonitor = useMutation({
    mutationFn: (m: PacketLossMonitor) => deletePacketLossMonitor(m.id),
    onSuccess: () => {
      showToast("Monitor deleted", "success");
      setRemoving(null);
      refreshMonitors();
    },
    onError: fail("Could not delete the monitor"),
  });
  const editMonitor = (m: PacketLossMonitor) => {
    let scheduleType: "interval" | "exact" = "interval";
    let exactTimes: string[] = [];
    let interval = m.interval;
    if (m.interval.startsWith("exact:")) {
      scheduleType = "exact";
      exactTimes = m.interval.slice(6).split(",").map((x) => x.trim());
      interval = "1h";
    }
    setEditing(m);
    setFormData({ host: m.host, name: m.name || "", interval, scheduleType, exactTimes, packetCount: m.packetCount, threshold: m.threshold, enabled: m.enabled });
    setShowForm(true);
  };
  const addMonitor = () => {
    setEditing(null);
    setFormData(defaultFormData);
    setShowForm(true);
  };

  const histories = useQueries({
    queries: monitors.map((m) => ({
      queryKey: ["net-overview-pl", m.id],
      queryFn: () => getPacketLossHistory(m.id, 1, 30),
      refetchInterval: 60_000,
    })),
  });

  // Cloudflare is the fast, always-available server; "nearest" tended to pick a slow small one.
  const run = useMutation({
    mutationFn: () =>
      loc.agentId
        ? runSpeedTest({ agentId: loc.agentId, serverIds: [], enableDownload: true, enableUpload: true })
        : runSpeedTest({ serverIds: ["cloudflare"], enableDownload: true, enableUpload: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["net-overview-speed"] });
      queryClient.invalidateQueries({ queryKey: ["home-speedtest-latest"] });
      showToast("Speed test complete", "success");
    },
    onError: (e: Error) => showToast("Speed test failed", "error", { description: e.message }),
  });

  // The main servers: Cloudflare plus a few well-connected Ookla hosts near this panel.
  const { data: serverList = [] } = useQuery({
    queryKey: ["speed-servers"],
    queryFn: () => getServers("speedtest") as Promise<{ id: string; sponsor: string; name: string }[]>,
    staleTime: 6 * 3600_000,
    retry: false,
  });
  const mainServers = useMemo(() => {
    const wanted = ["telia", "telenor", "bahnhof", "retn"];
    const picked: { id: string; label: string }[] = [{ id: "cloudflare", label: "Cloudflare" }];
    // an agent's default test is Cloudflare; it can also reach Russian mirrors
    if (loc.agentId) return [{ id: "", label: "Cloudflare" }, { id: "ru-yandex", label: "Yandex Mirror" }, { id: "ru-truenetwork", label: "TrueNetwork Mirror" }];
    for (const w of wanted) {
      const s = serverList.find((x) => x.sponsor?.toLowerCase().includes(w));
      if (s) picked.push({ id: s.id, label: s.sponsor });
    }
    return picked;
  }, [serverList, loc.agentId, loc.label]);
  const [progress, setProgress] = useState<string | null>(null);
  const runAll = useMutation({
    mutationFn: async () => {
      let ok = 0;
      for (let i = 0; i < mainServers.length; i++) {
        setProgress(`${i + 1} / ${mainServers.length} · ${mainServers[i].label}`);
        try {
          await runSpeedTest({
            ...(loc.agentId ? { agentId: loc.agentId, serverIds: mainServers[i].id ? [mainServers[i].id] : [] } : { serverIds: [mainServers[i].id] }),
            enableDownload: true,
            enableUpload: true,
          });
          ok++;
          queryClient.invalidateQueries({ queryKey: ["net-overview-speed"] });
        } catch {
          /* keep going: one slow server must not stop the rest */
        }
      }
      return ok;
    },
    onSuccess: (ok) => showToast(`Measured ${ok} of ${mainServers.length} servers`, ok > 0 ? "success" : "error"),
    onSettled: () => {
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ["home-speedtest-latest"] });
    },
  });

  const servers = useMemo(() => groupByServer(speed?.data ?? []), [speed]);
  const [picked, setPicked] = useState<string | null>(null);
  // default focus: Cloudflare when it has data, else the best server
  const focus = servers.find((s) => s.name === picked) ?? servers.find((s) => s.name === "Cloudflare") ?? servers[0];

  // Oldest -> newest series.
  const series = focus?.rows ?? [];
  const latest = series[series.length - 1];
  const earlier = series.slice(0, -1);
  const avg = (f: (r: SpeedTestResult) => number) =>
    earlier.length ? earlier.reduce((n, r) => n + f(r), 0) / earlier.length : 0;

  const cards = monitors.map((m, i) => {
    const rows: PacketLossResult[] = [...(histories[i]?.data?.data ?? [])].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const last = rows[rows.length - 1];
    const loss = last?.packetLoss ?? 0;
    const tone: Tone = !m.enabled
      ? "idle"
      : !last
        ? "idle"
        : loss >= Math.max(m.threshold, 1)
          ? "crit"
          : loss > 0
            ? "warn"
            : "ok";
    return { m, rows, last, loss, tone };
  });
  const healthy = cards.filter((c) => c.tone === "ok").length;
  const worst = cards.reduce<(typeof cards)[number] | null>(
    (w, c) => (!w || c.loss > w.loss ? c : w),
    null,
  );
  const qualityTone: Tone = cards.some((c) => c.tone === "crit")
    ? "crit"
    : cards.some((c) => c.tone === "warn") || anomalies.length > 0
      ? "warn"
      : cards.length
        ? "ok"
        : "idle";

  return (
    <div className="@container space-y-5">
      {/* headline numbers */}
      <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
        <Kpi
          icon={<ArrowDownIcon className="h-4 w-4" />}
          label="Download"
          value={latest ? mbps(latest.downloadSpeed) : "—"}
          sub={latest ? <Delta now={latest.downloadSpeed} base={avg((r) => r.downloadSpeed)} /> : "no tests yet"}
        />
        <Kpi
          icon={<ArrowUpIcon className="h-4 w-4" />}
          label="Upload"
          value={latest ? mbps(latest.uploadSpeed) : "—"}
          sub={latest ? <Delta now={latest.uploadSpeed} base={avg((r) => r.uploadSpeed)} /> : "no tests yet"}
        />
        <Kpi
          icon={<BoltIcon className="h-4 w-4" />}
          label="Ping"
          value={latest ? `${pingOf(latest).toFixed(0)} ms` : "—"}
          sub={latest ? <Delta now={pingOf(latest)} base={avg(pingOf)} invert /> : "no tests yet"}
        />
        <Kpi
          icon={<SignalIcon className="h-4 w-4" />}
          label="Connection quality"
          tone={qualityTone}
          value={cards.length ? `${healthy}/${cards.length}` : "—"}
          sub={
            cards.length === 0
              ? "no monitors yet"
              : worst && worst.loss > 0
                ? `worst: ${worst.m.name || worst.m.host} · ${worst.loss.toFixed(1)}% loss`
                : "no packet loss"
          }
        />
      </div>

      <div className="grid gap-5 @5xl:grid-cols-3 [&>*]:min-w-0">
        {/* speed by server */}
        <section className={cn(card, "border-gray-200 p-5 dark:border-white/10 @5xl:col-span-2")}>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white">Speed by server</h3>
              <p className="text-[12px] text-gray-500 dark:text-gray-400">
                {servers.length > 0
                  ? `${servers.length} servers · ${servers.reduce((n, x) => n + x.rows.length, 0)} tests from this panel's connection`
                  : "run a test to start the history"}
              </p>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onOpen("speedtest")}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
              >
                <ClockIcon className="h-3.5 w-3.5" />
                Schedule & servers
              </button>
              <button
                type="button"
                onClick={() => runAll.mutate()}
                disabled={runAll.isPending || run.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
              >
                {runAll.isPending ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                {runAll.isPending && progress ? progress : "Test all servers"}
              </button>
              <button
                type="button"
                onClick={() => run.mutate()}
                disabled={run.isPending || runAll.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[12.5px] font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {run.isPending ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                {run.isPending ? "Testing…" : "Run test now"}
              </button>
            </div>
          </div>

          {locations.length > 1 && (
            <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Where the test runs">
              {locations.map((l, i) => {
                const rows = speedHistories[i]?.data?.data ?? [];
                const last = rows.find((r: SpeedTestResult) => r.downloadSpeed > 0);
                const on = l.key === loc.key;
                return (
                  <button
                    key={l.key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setWhere(l.key)}
                    className={cn(
                      "flex min-w-[170px] flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors",
                      on ? "border-emerald-500/60 bg-emerald-500/10" : "border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.04]",
                    )}
                  >
                    <span className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">{l.label}</span>
                    <span className="text-[11.5px] tabular-nums text-gray-500 dark:text-gray-400">
                      {last ? `↓ ${last.downloadSpeed.toFixed(0)} · ↑ ${last.uploadSpeed.toFixed(0)} Mbps` : "no tests yet"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {servers.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-gray-500 dark:text-gray-400">Not enough data yet</div>
          ) : (
            <div className="space-y-5">
              <SpeedByServer servers={servers} selected={focus?.name ?? ""} onSelect={setPicked} />
              <div>
                <div className="mb-2 text-[12px] font-medium text-gray-700 dark:text-gray-300">{focus?.name}</div>
                <SpeedCharts rows={series} />
              </div>
            </div>
          )}
        </section>

        {/* what needs attention */}
        <section className={cn(card, "flex flex-col border-gray-200 p-5 dark:border-white/10")}>
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white">Needs attention</h3>
          <p className="mb-3 text-[12px] text-gray-500 dark:text-gray-400">anomalies and monitors above their threshold</p>
          {anomalies.length === 0 && !cards.some((c) => c.tone === "crit" || c.tone === "warn") ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-1 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <span className="text-emerald-500">●</span>
              The connection looks healthy
            </div>
          ) : (
            <ul className="space-y-2">
              {cards
                .filter((c) => c.tone === "crit" || c.tone === "warn")
                .map((c) => (
                  <li key={`m-${c.m.id}`} className="flex items-center gap-2.5 rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
                    <span className={cn("h-2 w-2 flex-none rounded-full", TONE_DOT[c.tone])} />
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                      {c.m.name || c.m.host}
                    </span>
                    <span className={cn("text-[12px] tabular-nums", TONE_TEXT[c.tone])}>{c.loss.toFixed(1)}% loss</span>
                  </li>
                ))}
              {anomalies.map((a, i) => (
                <li key={`a-${i}`} className="flex items-center gap-2.5 rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
                  <span className="h-2 w-2 flex-none rounded-full bg-amber-500" />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                    {a.monitor}
                  </span>
                  <span className="text-[12px] tabular-nums text-amber-600 dark:text-amber-400">
                    {a.metric} {a.current}
                    {a.unit} vs {a.baseline}
                    {a.unit}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="grid min-w-0 gap-5 @5xl:grid-cols-2 [&>*]:min-w-0">
        <ScheduleOverview onManage={() => onOpen("speedtest")} />
        <LiveRequestsCard onOpen={() => onOpen("requests")} />
      </div>

      {/* monitors */}
      <section>
        <div className="mb-3 flex items-center gap-3">
          <div>
            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white">Quality monitors</h3>
            <p className="text-[12px] text-gray-500 dark:text-gray-400">
              hosts pinged on a schedule to catch packet loss and latency spikes
            </p>
          </div>
          <button
            type="button"
            onClick={() => addMonitor()}
            className="ml-auto inline-flex h-8 flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add monitor
          </button>
        </div>

        {cards.length === 0 ? (
          <div className={cn(card, "border-dashed border-gray-300 p-8 text-center dark:border-white/15")}>
            <p className="text-sm text-gray-600 dark:text-gray-300">No monitors yet.</p>
            <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
              Add one for a host you care about (a DNS server, your provider's gateway) and it will show up here.
            </p>
            <button
              type="button"
              onClick={() => addMonitor()}
              className="mt-4 inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[12.5px] font-medium text-white hover:bg-blue-700"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add a monitor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @5xl:grid-cols-4">
            {cards.map(({ m, rows, last, loss, tone }) => (
              <div
                key={m.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetail(m)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setDetail(m)}
                className={cn(
                  card,
                  "group flex flex-col gap-2 p-4 text-left transition-colors hover:border-blue-500/50",
                  TONE_RING[tone],
                )}
              >
                <div className="flex items-center gap-2">
                  <span className={cn("h-2 w-2 flex-none rounded-full", TONE_DOT[tone])} />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-100">
                    {m.name || m.host}
                  </span>
                  <span className="flex flex-none items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      aria-label={m.enabled ? "Pause monitor" : "Start monitor"}
                      title={m.enabled ? "Pause" : "Start"}
                      disabled={toggleMonitor.isPending}
                      onClick={() => toggleMonitor.mutate(m)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-500/10 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {m.enabled ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      aria-label="Edit monitor"
                      title="Edit"
                      onClick={() => editMonitor(m)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-500/10 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete monitor"
                      title="Delete"
                      onClick={() => setRemoving(m)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-red-500/10 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </span>
                </div>
                <div className="truncate text-[11.5px] text-gray-500 dark:text-gray-400">
                  {m.host} · every {m.interval}
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className={cn("text-[22px] font-semibold leading-none tabular-nums", TONE_TEXT[tone])}>
                      {last ? `${loss.toFixed(1)}%` : "—"}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">packet loss</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[15px] font-semibold leading-none tabular-nums text-gray-900 dark:text-gray-100">
                      {last ? `${last.avgRtt.toFixed(0)} ms` : "—"}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">avg latency</div>
                  </div>
                </div>
                <Sparkline
                  values={rows.map((r) => r.avgRtt)}
                  color={tone === "crit" ? "#d4574b" : tone === "warn" ? "#e0a63a" : "#5fa37a"}
                  height={34}
                  name="latency"
                  format={(v) => `${v.toFixed(0)} ms`}
                  heads={rows.map((r) => new Date(r.createdAt).toLocaleString())}
                  emptyLabel={m.enabled ? "collecting…" : "paused"}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <PacketLossMonitorForm
        showForm={showForm}
        onClose={closeForm}
        onSubmit={(data) => saveMonitor.mutate(data)}
        editingMonitor={editing}
        formData={formData}
        onFormDataChange={setFormData}
        isLoading={saveMonitor.isPending}
      />

      <Dialog open={detail !== null} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{detail?.name || detail?.host}</DialogTitle>
          </DialogHeader>
          {detail && <PacketLossMonitorDetails key={detail.id} selectedMonitor={detail} monitorStatuses={monitorStatuses} />}
        </DialogContent>
      </Dialog>

      <Dialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete monitor?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-300">{`${removing?.name || removing?.host} and its history will be removed.`}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setRemoving(null)} className="h-9 rounded-lg border border-gray-200 px-3 text-sm dark:border-white/10">
              Cancel
            </button>
            <button
              type="button"
              disabled={removeMonitor.isPending}
              onClick={() => removing && removeMonitor.mutate(removing)}
              className="h-9 rounded-lg bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
